import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ListFilter, Grid, List, Search } from "lucide-react";
import { useUserListings } from "@/hooks/useUserListings";
import { useNavigate } from "react-router-dom";
import ListingHeader from "@/components/dashboard/listings/ListingHeader";
import ListingsContent from "@/components/dashboard/listings/ListingsContent";
import { ListingCore, UserRole, Listing } from "@/lib/types"; // Import ListingCore, UserRole, and Listing types
import { formatCurrency } from "@/lib/formatters"; // Import formatCurrency
import { useAuth } from "@/context/AuthContext"; // Import useAuth
import { useMutation, useQueryClient } from "@tanstack/react-query"; // Import useMutation, useQueryClient
import {
  publishUserListing,
  deleteUserListing,
  toggleListingFeatured, // Import new API function
} from "@/lib/api/listings/userListingsApi"; // Import API functions
import { toast } from "sonner"; // Import toast

const DashboardListings = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [displayMode, setDisplayMode] = useState<"list" | "grid">("list");
  const [filterStatus, setFilterStatus] = useState("all"); // New state for filter

  const { user } = useAuth(); // Get user from auth context
  const queryClient = useQueryClient(); // Get query client for invalidation

  const publishMutation = useMutation({
    mutationFn: publishUserListing,
    onSuccess: () => {
      toast.success("Listing published successfully!");
      queryClient.invalidateQueries({ queryKey: ["userListings"] });
    },
    onError: (error) => {
      toast.error(`Failed to publish listing: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUserListing,
    onSuccess: () => {
      toast.success("Listing deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["userListings"] });
    },
    onError: (error) => {
      toast.error(`Failed to delete listing: ${error.message}`);
    },
  });

  const featureToggleMutation = useMutation({
    mutationFn: toggleListingFeatured,
    onSuccess: () => {
      toast.success("Listing featured status updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["userListings"] });
    },
    onError: (error) => {
      toast.error(`Failed to update featured status: ${error.message}`);
    },
  });

  const {
    data: publishedData,
    isLoading: isLoadingPublished,
    isError: isErrorPublished,
    error: errorPublished,
  } = useUserListings("published");
  const {
    data: draftData,
    isLoading: isLoadingDraft,
    isError: isErrorDraft,
    error: errorDraft,
  } = useUserListings("draft");
  const {
    data: pendingData,
    isLoading: isLoadingPending,
    isError: isErrorPending,
    error: errorPending,
  } = useUserListings("pending");

  const isLoading = isLoadingPublished || isLoadingDraft || isLoadingPending;
  const isError = isErrorPublished || isErrorDraft || isErrorPending;
  const error = errorPublished || errorDraft || errorPending;

  const publishedListings = publishedData || [];
  const draftListings = draftData || [];
  const pendingListings = pendingData || [];

  console.log("Published Data:", publishedListings);
  console.log("Draft Data:", draftListings);
  console.log("Pending Data:", pendingListings);

  // Combine all user listings for display, ensuring uniqueness by ID
  const allUserListings: Listing[] = useMemo(() => {
    return Array.from(
      new Map(
        [...publishedListings, ...draftListings, ...pendingListings].map(
          (listingCore) => {
            // Map ListingCore to Listing, adding the 'featured' property
            const listing: Listing = {
              ...listingCore,
              featured: !!listingCore.is_featured, // Derive 'featured' from 'is_featured'
            };
            return [listing.id, listing];
          }
        )
      ).values()
    ).sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [publishedListings, draftListings, pendingListings]); // Dependencies for memoization

  // Filter listings based on search query and status
  const filteredListings = useMemo(() => {
    return allUserListings.filter((listing) => {
      const matchesSearch =
        listing.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (listing.address &&
          listing.address.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (listing.listing_type &&
          listing.listing_type
            .toLowerCase()
            .includes(searchQuery.toLowerCase()));

      const matchesStatus =
        filterStatus === "all" || listing.status === filterStatus;

      return matchesSearch && matchesStatus;
    });
  }, [allUserListings, searchQuery, filterStatus]); // Dependencies for memoization

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        Loading your listings...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-8 text-center text-red-500">
        Error loading listings: {error?.message || "Unknown error"}
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <ListingHeader />

      {/* Count Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <h3 className="text-lg font-semibold text-gray-700">
            Draft Listings
          </h3>
          <p className="text-3xl font-bold text-blue-600">
            {draftListings.length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <h3 className="text-lg font-semibold text-gray-700">
            Pending Listings
          </h3>
          <p className="text-3xl font-bold text-yellow-600">
            {pendingListings.length}
          </p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md text-center">
          <h3 className="text-lg font-semibold text-gray-700">
            Published Listings
          </h3>
          <p className="text-3xl font-bold text-green-600">
            {publishedListings.length}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <Input
            type="search"
            placeholder="Search your listings..."
            className="pl-9 max-w-sm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            value={displayMode}
            onValueChange={(value) =>
              value && setDisplayMode(value as "list" | "grid")
            }
          >
            <ToggleGroupItem value="grid" aria-label="Grid view">
              <Grid className="h-4 w-4" />
            </ToggleGroupItem>
            <ToggleGroupItem value="list" aria-label="List view">
              <List className="h-4 w-4" />
            </ToggleGroupItem>
          </ToggleGroup>

          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[180px]">
              <ListFilter className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Listings</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <ListingsContent
        key={filteredListings.map((l) => l.id).join("-")} // Update key to prevent unnecessary re-renders on feature toggle
        listings={filteredListings}
        searchQuery={searchQuery}
        displayMode={displayMode}
        onClearSearch={() => setSearchQuery("")}
        userRole={user?.role as UserRole} // Cast user?.role to UserRole
        onPublish={publishMutation.mutate} // Pass publish mutation
        onDelete={deleteMutation.mutate} // Pass delete mutation
        onFeatureToggle={featureToggleMutation.mutate} // Pass feature toggle mutation
        isPublishing={publishMutation.isPending} // Pass isPublishing
        isDeleting={deleteMutation.isPending} // Pass isDeleting
        isFeatureToggling={featureToggleMutation.isPending} // Pass isFeatureToggling
      />
    </div>
  );
};

export default DashboardListings;
